<template>
  <div class="product-label-question">
    <div>
      <img v-if="node.question.image !== null" :src="node.question.image" class="question-image" />
      <p v-if="node.prompt !== null" v-html="node.prompt"></p>
      <div v-html="node.question.instructions"></div>
      <v-file-input
        id="image-input"
        v-model="content"
        :label="node.question.product_label_prompt"
        accept="image/jpeg"
        capture="environment"
        outlined
        prepend-icon="mdi-camera"
        :hint="node.question.help_text"
        persistent-hint
        @change="onCaptureLabel"
      ></v-file-input>
    </div>
    <p v-if="productLabel !== null && productLabel['status'] === 'processing'" class="processing">
      <img src="../assets/gear.gif" alt="processing..." class="busy" />
      Please wait, reading the label...
      </p>
    <div v-if="productLabel !== null && productLabel.hasOwnProperty('coa')" class="label">
      <p>
        <strong>Product Information</strong>
        <table class="product-info">
          <tr v-for="field in filteredProductInfoFields" :key="field">
            <td class="product-info-field">{{ fieldInfo[field].label }}</td>
            <td class="product-info-value">
              {{ productLabel.coa[field] }}<span v-if="fieldInfo[field].type === 'percent'">%</span>
            </td>
          </tr>
        </table>
      </p>
      <p>
        To help us improve our automatic parsing, please let us know how well this label was parsed.
      </p>
      <v-select
        @change="onChangeRating"
        :items="ratingOptions"
        label="Select option..."
        item-text="label"
        item-value="id"
        v-model="rating"
        outlined
      ></v-select>
    </div>
    <div v-if="productLabel !== null && productLabel['status'] === 'error'" class="error">
      There was an error while processing the label image. You can try again or just submit this
      label.
    </div>
    <div class="buttons">
      <v-btn
        class="submit-button"
        :disabled="!isValidSelection()"
        color="primary"
        @click="onAnswerQuestion"
      >
        {{ submitButton }}
      </v-btn>
      <v-btn v-if="node.skippable" class="submit-button" color="secondary" @click="onSkipQuestion">
        {{ skipButton }}
      </v-btn>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ProductLabelQuestion',
  data() {
    return {
      content: null,
      rating: null,
      ratingOptions: [
        {
          id: 0,
          value: 0,
          label: 'Everything looks correct',
        },
        {
          id: 1,
          value: 1,
          label: 'A few missing fields or incorrect values',
        },
        {
          id: 2,
          value: 2,
          label: 'Many missing fields or incorrect values',
        },
      ],
      fieldInfo: {
        product_name: { label: 'Product Name', type: 'str' },
        manufacturer: { label: 'Manufacturer', type: 'str' },
        product_type: { label: 'Product Type', type: 'str' },
        batch_id: { label: 'Batch ID', type: 'str' },
        report_date: { label: 'Report Date', type: 'str' },
        total_cbd: { label: 'Total CBD', type: 'percent' },
        total_thc: { label: 'Total THC', type: 'percent' },
        total_cannabinoids: { label: 'Total Cannabinoids', type: 'percent' },
        total_terpenes: { label: 'Total Terpenes', type: 'percent' },
        delta_9_thc: { label: 'Delta 9 THC', type: 'percent' },
        delta_9_thca: { label: 'Delta 9 THCA', type: 'percent' },
        delta_9_thcv: { label: 'Delta 9 THCV', type: 'percent' },
        delta_9_thcva: { label: 'Delta 9 THCVA', type: 'percent' },
        cbd: { label: 'CBD', type: 'percent' },
        cbda: { label: 'CBDA', type: 'percent' },
        cbdv: { label: 'CBDV', type: 'percent' },
        cbdva: { label: 'CBDVA', type: 'percent' },
        cbn: { label: 'CBN', type: 'percent' },
        cbna: { label: 'CBNA', type: 'percent' },
        cbc: { label: 'CBC', type: 'percent' },
        cbca: { label: 'CBCA', type: 'percent' },
        cbg: { label: 'CBG', type: 'percent' },
        cbga: { label: 'CBGA', type: 'percent' },
        cbl: { label: 'CBL', type: 'percent' },
        cbla: { label: 'CBLA', type: 'percent' },
        delta_8_thc: { label: 'Delta 8 THC', type: 'percent' },
        delta_8_thca: { label: 'Delta 8 THCA', type: 'percent' },
        delta_8_thcv: { label: 'Delta 8 THCV', type: 'percent' },
        delta_8_thcva: { label: 'Delta 8 THCVA', type: 'percent' },
        delta_10_thc: { label: 'Delta 10 THC', type: 'percent' },
        delta_10_thca: { label: 'Delta 10 THCA', type: 'percent' },
        delta_10_thcv: { label: 'Delta 10 THCV', type: 'percent' },
        delta_10_thcva: { label: 'Delta 10 THCVA', type: 'percent' },
        nine_s_hhc: { label: '9S-HHC', type: 'percent' },
        nine_r_hhc: { label: '9R-HHC', type: 'percent' },
        alpha_bisabolol: { label: 'Alpha Bisabolol', type: 'percent' },
        alpha_cedrene: { label: 'Alpha Cedrene', type: 'percent' },
        alpha_humulene: { label: 'Alpha Humulene', type: 'percent' },
        alpha_pinene: { label: 'Alpha Pinene', type: 'percent' },
        alpha_terpinene: { label: 'Alpha Terpinene', type: 'percent' },
        alpha_terpinolene: { label: 'Alpha Terpinolene', type: 'percent' },
        alpha_phellandrene: { label: 'Alpha Phellandrene', type: 'percent' },
        beta_caryophyllene: { label: 'Beta Caryophyllene', type: 'percent' },
        beta_caryophyllene_oxide: { label: 'Beta Caryophyllene Oxide', type: 'percent' },
        beta_myrcene: { label: 'Beta Myrcene', type: 'percent' },
        beta_pinene: { label: 'Beta Pinene', type: 'percent' },
        camphene: { label: 'Camphene', type: 'percent' },
        cedrol: { label: 'Cedrol', type: 'percent' },
        cineole: { label: 'Cineole', type: 'percent' },
        cis_nerolidol: { label: 'Cis Nerolidol', type: 'percent' },
        cis_ocimene: { label: 'Cis Ocimene', type: 'percent' },
        eucalyptol: { label: 'Eucalyptol', type: 'percent' },
        farnesene: { label: 'Farnesene', type: 'percent' },
        fenchol: { label: 'Fenchol', type: 'percent' },
        fenchone: { label: 'Fenchone', type: 'percent' },
        gamma_terpinene: { label: 'Gamma Terpinene', type: 'percent' },
        geraniol: { label: 'Geraniol', type: 'percent' },
        geranyl_acetate: { label: 'Geranyl Acetate', type: 'percent' },
        guaiol: { label: 'Guaiol', type: 'percent' },
        hexahydrothymol: { label: 'Hexahydrothymol', type: 'percent' },
        isoborneol: { label: 'Isoborneol', type: 'percent' },
        isopulegol: { label: 'Isopulegol', type: 'percent' },
        limonene: { label: 'Limonene', type: 'percent' },
        linalool: { label: 'Linalool', type: 'percent' },
        sabinene: { label: 'Sabinene', type: 'percent' },
        sabinene_hydrate: { label: 'Sabinene Hydrate', type: 'percent' },
        terpineol: { label: 'Terpineol', type: 'percent' },
        three_carene: { label: '3 Carene', type: 'percent' },
        trans_caryophyllene: { label: 'Trans Caryophyllene', type: 'percent' },
        trans_nerolidol: { label: 'Trans Nerolidol', type: 'percent' },
        trans_ocimene: { label: 'Trans Ocimene', type: 'percent' },
      },
    };
  },
  methods: {
    isValidSelection() {
      return this.content !== null && this.content !== undefined;
    },
    onCaptureLabel() {
      this.$emit('capture', { node: this.node, image: this.content });
    },
    onAnswerQuestion() {
      const result = {
        url: this.productLabel.url,
        coa: this.productLabel.coa,
        rating: this.rating.defineComponent,
      };
      this.$emit('answer', { node: this.node, value: this.rating.id, text: JSON.stringify(result) });
      this.content = null;
    },
    onSkipQuestion() {
      this.$emit('skip');
      this.content = null;
    },
  },
  computed: {
    filteredProductInfoFields() {
      // Skip coa keys absent from fieldInfo — fieldInfo[field].label/.type would throw on render.
      return Object.keys(this.productLabel.coa).filter(
        (field) => field in this.fieldInfo && this.productLabel.coa[field] !== null,
      );
    },
  },
  props: {
    node: Object,
    productLabel: Object,
    submitButton: String,
    skipButton: String,
  },
};
</script>

<style lang="scss" scoped>
@import '../styles/_variables.scss';

.product-label-question {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  min-height: 100%;
  padding: 1.5rem 1.5rem 1.5rem 1.5rem;

  .processing {
    margin-top: 10px;
  }

  .busy {
    height: 16px;
  }

  .message {
    margin-top: 10px;
  }

  .question-image {
    display: block;
    max-width: 340px;
    margin-left: auto;
    margin-right: auto;
    margin-bottom: 2rem;
  }

  .buttons {
    display: flex;
    width: 100%;
    flex-direction: row-reverse;
    justify-content: space-between;
    align-self: center;
  }

  .product-info {
    border: 1px solid #999;
    width: 100%;
    border-collapse:collapse;

    td {
      border: 1px solid #999;
      font-size: 0.9rem;
      padding: 1px 3px 1px 3px;
    }

    tr:nth-child(even) {
      background-color: #DDD
    }

    .product-info-value {
      text-align: right;
    }
  }
}

@media (min-width: $breakpoint-tablet) {
  .product-label-question {
    justify-content: flex-start;

    .buttons {
      margin-top: 3rem;
    }
  }
}
</style>
